// Arcade course progress: one record per cabinet, persisted per scenario in
// localStorage so a learner can walk away, reload and resume. The frog
// verdict and the personalised debrief read only from here.

import { useSyncExternalStore } from "react";
import { useSim } from "../../state/store";

export type ArcadeGameId = "turkeyhunt" | "spotfraud" | "invaders";

/** Share of assessable decisions a cabinet needs to keep its frog support. */
export const PASS_RATIO: Record<ArcadeGameId, number> = {
  turkeyhunt: 0.7,
  spotfraud: 0.8,
  invaders: 0.75,
};

/** Supports under the frog's springboard; one per cabinet. */
export const FROG_SUPPORTS = 3;
/** The frog is saved while at least this many supports hold. */
export const FROG_MIN_SUPPORTS = 2;

export interface KnowledgeMistake {
  /** Short topic label, e.g. "Bank detail change". */
  topic: string;
  /** One sentence the debrief can show as the takeaway. */
  lesson: string;
}

export interface CabinetResult {
  gameId: ArcadeGameId;
  completed: boolean;
  rawScore: number;
  /** 0..100, share of assessable decisions that were right. */
  normalizedCoursePoints: number;
  correctCount: number;
  incorrectCount: number;
  totalAssessableDecisions: number;
  knowledgeMistakes: KnowledgeMistake[];
  passedCabinetThreshold: boolean;
  /** 1 when the cabinet failed its threshold (one frog support lost), else 0. */
  supportPenalty: 0 | 1;
  finishedAt: string;
}

/** guided: the camera takes the learner from station to station. free: WASD walk. */
export type ArcadeMoveMode = "guided" | "free";

export interface ArcadeProgress {
  v: 1;
  briefed: boolean;
  results: Partial<Record<ArcadeGameId, CabinetResult>>;
  mode?: ArcadeMoveMode;
}

const EMPTY: ArcadeProgress = { v: 1, briefed: false, results: {} };
const key = (scenarioId: string) => `cf-arcade:${scenarioId}`;
const cache = new Map<string, ArcadeProgress>();
const listeners = new Set<() => void>();

export function makeResult(
  gameId: ArcadeGameId,
  r: { rawScore: number; correct: number; incorrect: number; total?: number; mistakes?: KnowledgeMistake[]; completed?: boolean },
): CabinetResult {
  const total = Math.max(0, r.total ?? r.correct + r.incorrect);
  const ratio = total > 0 ? r.correct / total : 0;
  const passed = total > 0 && ratio + 1e-9 >= PASS_RATIO[gameId];
  return {
    gameId,
    completed: r.completed ?? true,
    rawScore: Math.max(0, Math.round(r.rawScore)),
    normalizedCoursePoints: Math.round(ratio * 100),
    correctCount: r.correct,
    incorrectCount: r.incorrect,
    totalAssessableDecisions: total,
    knowledgeMistakes: dedupeMistakes(r.mistakes ?? []),
    passedCabinetThreshold: passed,
    supportPenalty: passed ? 0 : 1,
    finishedAt: new Date().toISOString(),
  };
}

function dedupeMistakes(list: KnowledgeMistake[]): KnowledgeMistake[] {
  const seen = new Set<string>();
  const out: KnowledgeMistake[] = [];
  for (const m of list) {
    const k = m.topic.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(m);
  }
  return out;
}

export function loadProgress(scenarioId: string): ArcadeProgress {
  const hit = cache.get(scenarioId);
  if (hit) return hit;
  let p: ArcadeProgress = EMPTY;
  try {
    const raw = localStorage.getItem(key(scenarioId));
    if (raw) {
      const parsed = JSON.parse(raw) as ArcadeProgress;
      if (parsed && parsed.v === 1 && parsed.results) p = { ...EMPTY, ...parsed };
    }
  } catch {
    /* private mode or bad JSON: start fresh */
  }
  cache.set(scenarioId, p);
  return p;
}

function write(scenarioId: string, p: ArcadeProgress) {
  cache.set(scenarioId, p);
  try {
    localStorage.setItem(key(scenarioId), JSON.stringify(p));
  } catch {
    /* ignore quota errors; in-memory copy still drives this session */
  }
  listeners.forEach((l) => l());
}

/**
 * A run that reached its end (all rounds played, or game over) always replaces
 * the saved result. A run left early only counts while the cabinet has no
 * result yet, so an abandoned first try is recorded as not passed and a later
 * abandoned replay never changes a result already earned.
 */
export function saveCabinetResult(scenarioId: string, r: CabinetResult, finished = true) {
  const p = loadProgress(scenarioId);
  if (!finished && p.results[r.gameId]) return;
  write(scenarioId, { ...p, results: { ...p.results, [r.gameId]: r } });
}

export function markBriefed(scenarioId: string) {
  const p = loadProgress(scenarioId);
  if (!p.briefed) write(scenarioId, { ...p, briefed: true });
}

export function clearProgress(scenarioId: string, keepBriefing = true) {
  const p = loadProgress(scenarioId);
  write(scenarioId, { v: 1, briefed: keepBriefing ? p.briefed : false, results: {}, mode: p.mode });
}

/** New run: nothing played, briefing not seen, chosen way of moving. */
export function startRun(scenarioId: string, mode: ArcadeMoveMode) {
  write(scenarioId, { v: 1, briefed: false, results: {}, mode });
}

/** Keep the saved run, only change the way of moving. */
export function setMoveMode(scenarioId: string, mode: ArcadeMoveMode) {
  const p = loadProgress(scenarioId);
  write(scenarioId, { ...p, mode });
}

export function useArcadeProgress(scenarioId: string): ArcadeProgress {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => loadProgress(scenarioId),
    () => EMPTY,
  );
}

export const GAME_ORDER: ArcadeGameId[] = ["turkeyhunt", "spotfraud", "invaders"];

/** Every cabinet has a result (passed, failed or left early). */
export function allDone(p: ArcadeProgress): boolean {
  return GAME_ORDER.every((g) => !!p.results[g]);
}

/** Hub spot id of each cabinet (also the edge key that walks to it). */
export const SPOT_OF: Record<ArcadeGameId, string> = {
  turkeyhunt: "turkey",
  spotfraud: "fraud",
  invaders: "invaders",
};

/** Where the guided tour goes next: the TV before the briefing and after the last cabinet. */
export function nextTarget(p: ArcadeProgress): string {
  if (!p.briefed) return "tv";
  const g = GAME_ORDER.find((x) => !p.results[x]);
  return g ? SPOT_OF[g] : "tv";
}

/**
 * Leave an arcade device. In the guided tour the camera moves straight on to
 * the next station; a cabinet left before it has a result, and the TV after
 * the final score, go back to the hall instead (the default edge).
 */
export function exitArcadeDevice(onDone: () => void, from?: ArcadeGameId | "tv") {
  const sid = arcadeScenarioId();
  const p = loadProgress(sid);
  if (p.mode === "guided") {
    const fromGame = from && from !== "tv" ? from : undefined;
    const blocked = (fromGame && !p.results[fromGame]) || (from === "tv" && allDone(p));
    if (!blocked) {
      useSim.getState().advance(nextTarget(p));
      return;
    }
  }
  onDone();
}

export function supportsLeft(p: ArcadeProgress): number {
  let lost = 0;
  for (const g of GAME_ORDER) lost += p.results[g]?.supportPenalty ?? 0;
  return Math.max(0, FROG_SUPPORTS - lost);
}

export function frogSaved(p: ArcadeProgress): boolean {
  return supportsLeft(p) >= FROG_MIN_SUPPORTS;
}

export function courseTotal(p: ArcadeProgress): number {
  return GAME_ORDER.reduce((s, g) => s + (p.results[g]?.normalizedCoursePoints ?? 0), 0);
}

/** Scenario id of the running course (progress is stored per course). */
export function arcadeScenarioId(): string {
  return useSim.getState().scenario?.id ?? "arcade-hall";
}

/** Save a cabinet result for the running course (see saveCabinetResult for `finished`). */
export function saveResult(r: CabinetResult, opts: { finished?: boolean } = {}) {
  saveCabinetResult(arcadeScenarioId(), r, opts.finished ?? true);
}
